<template>
  <div class="modal d-block" tabindex="-1" @click.self="$emit('close')">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ t("ecolabels.upload_wizard.title") }}</h5>
          <button type="button" class="btn-close" @click="$emit('close')" />
        </div>
        <div class="modal-body">
          <section v-if="showNoAuthorizedSellerState" class="text-center py-5">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/b/b0/EKOenergy_logo.jpg"
              :alt="t('ecolabels.upload_wizard.ekoenergy_logo_alt')"
              class="ekoenergy-logo mb-4"
            />
            <h5 class="mb-2">
              {{ t("ecolabels.upload_wizard.no_authorized_seller_title") }}
            </h5>
            <p class="text-muted mb-3">
              {{
                t("ecolabels.upload_wizard.no_authorized_seller_description")
              }}
            </p>
            <a
              href="https://www.ekoenergy.org/"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-primary"
            >
              {{ t("ecolabels.upload_wizard.no_authorized_seller_cta") }}
            </a>
          </section>

          <template v-else>
            <section class="mb-4">
              <h6 class="fw-semibold mb-2">
                {{ t("ecolabels.upload_wizard.step1_label") }}
              </h6>
              <p class="text-muted small mb-3">
                {{ t("ecolabels.upload_wizard.step1_description") }}
              </p>
              <div class="mb-3">
                <label class="form-label fw-semibold">
                  {{ t("ecolabels.upload_wizard.seller_company") }}
                </label>
                <div class="d-flex gap-2">
                  <select
                    v-model="selectedCompanyId"
                    class="form-select"
                    :disabled="
                      uploading || saving || availableCompanies.length === 0
                    "
                  >
                    <option
                      v-for="company in availableCompanies"
                      :key="company.id"
                      :value="company.id"
                    >
                      {{ company.name }}
                    </option>
                  </select>
                  <template v-if="isAdmin">
                    <input
                      v-model="newCompanyName"
                      class="form-control"
                      :placeholder="
                        t('ecolabels.upload_wizard.new_company_placeholder')
                      "
                    />
                    <input
                      v-model="newCompanyEmail"
                      class="form-control"
                      type="email"
                      :placeholder="
                        t(
                          'ecolabels.upload_wizard.new_company_email_placeholder'
                        )
                      "
                    />
                    <button
                      class="btn btn-outline-primary"
                      :disabled="
                        creatingCompany ||
                        !newCompanyName.trim() ||
                        !newCompanyEmail.trim()
                      "
                      @click="createCompany"
                    >
                      <span
                        v-if="creatingCompany"
                        class="spinner-border spinner-border-sm"
                      />
                      <font-awesome-icon v-else icon="plus" />
                    </button>
                  </template>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-semibold">
                  {{ t("ecolabels.upload_wizard.cancellation_source") }}
                </label>
                <select
                  v-model="selectedExistingCancellationId"
                  class="form-select"
                  :disabled="
                    uploading ||
                    saving ||
                    !selectedCompanyId ||
                    loadingCancellations
                  "
                  @change="handleCancellationSourceChange"
                >
                  <option :value="null">
                    {{ t("ecolabels.upload_wizard.upload_new_statement") }}
                  </option>
                  <option
                    v-for="cancellation in existingCancellations"
                    :key="cancellation.id"
                    :value="cancellation.id"
                  >
                    {{ formatExistingCancellationLabel(cancellation) }}
                  </option>
                </select>
              </div>

              <AiChatCard
                v-if="!isUsingExistingCancellation && selectedCompanyId"
                :key="selectedCompanyId"
                :companyId="selectedCompanyId"
                endpoint="/api/ecolabels/statements/chat"
                :greeting="$t('ai_chat.greetings.ecolabels')"
                @analysis-complete="handleChatResult"
              />
              <div
                v-else-if="isUsingExistingCancellation"
                class="alert alert-info py-2 mb-3"
              >
                {{ t("ecolabels.upload_wizard.using_existing_cancellation") }}
              </div>
              <div v-if="uploading" class="d-flex justify-content-end mt-2">
                <span class="text-muted small">
                  <span class="spinner-border spinner-border-sm me-2" />
                  {{ t("common.loading") }}
                </span>
              </div>
            </section>

            <section v-if="!isUsingExistingCancellation" class="mb-4">
              <h6 class="fw-semibold mb-2">
                {{ t("ecolabels.upload_wizard.step2_label") }}
              </h6>
              <div class="card border-translucent">
                <div class="card-body">
                  <CancelGoStatementComponent
                    :statement="statementForm"
                    :statement-index="0"
                    :show-collapsible-header="false"
                    :show-remove-button="false"
                    :show-section-titles="true"
                    @update:statement="handleStatementUpdate"
                  />
                </div>
              </div>
            </section>

            <section>
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="fw-semibold mb-0">
                  {{ t("ecolabels.upload_wizard.step2_label") }}
                </h6>
                <button
                  class="btn btn-outline-primary btn-sm"
                  @click="showDevicePicker = true"
                >
                  <font-awesome-icon icon="plus" class="me-1" />
                  {{ t("ecolabels.upload_wizard.add_device_manually") }}
                </button>
              </div>

              <div v-if="showDevicePicker" class="card border-primary mb-3">
                <div class="card-body p-2">
                  <InputText
                    v-model="deviceSearchQuery"
                    class="w-100 mb-2"
                    :placeholder="t('ecolabels.upload_wizard.search_devices')"
                  />
                  <div class="picker-list">
                    <button
                      v-for="device in filteredPickerDevices"
                      :key="device.id"
                      class="btn btn-light w-100 text-start mb-1"
                      @click="addDeviceFromPicker(device)"
                    >
                      <span class="fw-semibold">{{ device.name }}</span>
                      <small class="text-muted ms-2">{{
                        device.country
                      }}</small>
                    </button>
                    <div
                      v-if="filteredPickerDevices.length === 0"
                      class="text-muted text-center py-2"
                    >
                      {{ t("ecolabels.upload_wizard.no_devices_found") }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="card border-translucent">
                <div class="card-body p-0">
                  <DataTable
                    v-model:filters="tableFilters"
                    :value="assignedDevices"
                    dataKey="device.id"
                    filterDisplay="row"
                    class="ecolabel-matching-datatable"
                    removableSort
                  >
                    <template #empty>
                      <div class="text-center py-4 text-muted">
                        {{ t("ecolabels.upload_wizard.no_devices_assigned") }}
                      </div>
                    </template>

                    <Column
                      field="device.name"
                      :header="t('ecolabels.table.name')"
                      sortable
                      :showFilterMenu="false"
                    >
                      <template #body="{ data }">
                        <div class="fw-semibold">{{ data.device.name }}</div>
                        <small
                          v-if="data.device.plant_ecolabel_id"
                          class="text-muted"
                          >{{ data.device.plant_ecolabel_id }}</small
                        >
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <InputText
                          v-model="filterModel.value"
                          class="p-column-filter w-100"
                          @input="filterCallback()"
                          :placeholder="
                            t('ecolabels.filters.search_placeholder')
                          "
                        />
                      </template>
                    </Column>

                    <Column
                      field="device.country"
                      :header="t('ecolabels.table.country')"
                      sortable
                      :showFilterMenu="false"
                    >
                      <template #body="{ data }">{{
                        data.device.country
                      }}</template>
                      <template #filter="{ filterModel, filterCallback }">
                        <InputText
                          v-model="filterModel.value"
                          class="p-column-filter w-100"
                          @input="filterCallback()"
                          :placeholder="t('ecolabels.filters.search')"
                        />
                      </template>
                    </Column>

                    <Column
                      field="mwh"
                      :header="t('ecolabels.upload_wizard.mwh')"
                      sortable
                      :showFilterMenu="false"
                    >
                      <template #body="{ data, index }">
                        <input
                          type="number"
                          class="form-control form-control-sm"
                          :value="data.mwh ?? ''"
                          min="0"
                          @input="updateAssignedMwh(index, $event)"
                        />
                      </template>
                      <template #filter="{ filterModel, filterCallback }">
                        <InputText
                          v-model="filterModel.value"
                          class="p-column-filter w-100"
                          @input="filterCallback()"
                          :placeholder="t('ecolabels.filters.search')"
                        />
                      </template>
                    </Column>

                    <Column
                      field="confidence"
                      :header="t('ecolabels.upload_wizard.confidence')"
                      sortable
                      :showFilterMenu="false"
                    >
                      <template #body="{ data }">
                        <span
                          v-if="data.confidence > 0"
                          :class="getConfidenceBadge(data.confidence)"
                        >
                          {{ Math.round(data.confidence * 100) }}%
                        </span>
                        <span v-else class="badge bg-secondary">Manual</span>
                      </template>
                    </Column>

                    <Column
                      :header="t('ecolabels.certificates.actions_col')"
                      :exportable="false"
                    >
                      <template #body="{ index }">
                        <button
                          class="btn btn-link btn-sm text-danger p-0"
                          @click="removeAssignedDevice(index)"
                        >
                          <font-awesome-icon icon="times" />
                        </button>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>
            </section>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="$emit('close')">
            {{ t("common.cancel") }}
          </button>
          <button
            v-if="!showNoAuthorizedSellerState"
            class="btn btn-primary"
            :disabled="
              saving ||
              !isMwhCancelledValid ||
              (isUsingExistingCancellation
                ? !uploadedStatement
                : !statementUploadId || !isStatementValid)
            "
            @click="saveStatement"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" />
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ t("ecolabels.upload_wizard.save_and_submit") }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" />
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import AiChatCard from "@/dashboard/inventory_management/common/AiChatCard.vue";
import CancelGoStatementComponent from "@/dashboard/inventory_management/company_orders/CancelGoStatementComponent.vue";
import type { BeneficiaryTypeEnum, IndividualCancelStatement } from "@/types";
import { normalizeRegistryCountryRegion } from "@/utils/registries";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type {
  AuthorizedSellerCompany,
  CancellationStatement,
  CompanyCancellationSummary,
  EcolabelDevice,
} from "../api/ecolabelsTypes";

interface AssignedDevice {
  device: EcolabelDevice;
  mwh: number | null;
  confidence: number;
}

/**
 * Payload returned by the AI chat conversation after analyzing a cancellation statement document.
 *
 * @property form_data - Extracted cancellation statement metadata (beneficiary, registry, period, purpose, total MWh, and optional notes)
 * @property ai_matches - Device matches suggested by AI with confidence scores and suggested MWh attributions
 * @property statement_upload_id - ID of the uploaded document used to create the statement on save (avoids re-upload). Null indicates chat was reset or no document uploaded yet.
 *
 * The chat can emit `null` to signal a reset; otherwise this structure is expected.
 * On invalid/malformed payloads, the wizard invokes the global error handler and resets to a clean state.
 */
interface EcolabelChatResult {
  form_data: {
    beneficiary_name: string | null;
    registry_code: string | null;
    country: string | null;
    period_start: string | null;
    period_end: string | null;
    purpose: string | null;
    total_mwh: number | null;
    notes: string | null;
  };
  ai_matches: Array<{
    device: EcolabelDevice;
    confidence: number;
    suggested_mwh: number;
  }>;
  statement_upload_id: number | null;
}

const props = defineProps<{
  companyId: number;
  allDevices: EcolabelDevice[];
  isAdmin: boolean;
}>();
const emit = defineEmits<{ close: []; uploaded: [] }>();
const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const uploading = ref(false);
const saving = ref(false);
const loadingCompanies = ref(false);
const loadingCancellations = ref(false);

// Set by the existing-cancellation path (statement is persisted up front by uploadStatement).
const uploadedStatement = ref<CancellationStatement | null>(null);
// Set by the chat path: the id of the document uploaded inside the chat conversation,
// used to create the statement from that file on save (no re-upload).
const statementUploadId = ref<number | null>(null);
const statementNotes = ref<string | null>(null);
const assignedDevices = ref<AssignedDevice[]>([]);

const showDevicePicker = ref(false);
const deviceSearchQuery = ref("");
const availableCompanies = ref<AuthorizedSellerCompany[]>([]);
const existingCancellations = ref<CompanyCancellationSummary[]>([]);
const selectedCompanyId = ref<number | null>(props.companyId || null);
const selectedExistingCancellationId = ref<number | null>(null);
const newCompanyName = ref("");
const newCompanyEmail = ref("");
const creatingCompany = ref(false);
const tableFilters = ref({
  "device.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
  "device.country": { value: null, matchMode: FilterMatchMode.CONTAINS },
  mwh: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const statementForm = ref<IndividualCancelStatement>({
  temp_id: "",
  beneficiary_name: "",
  beneficiary_type: "CONSUMER" as BeneficiaryTypeEnum,
  registry_code: "",
  registry_country_region: "",
  address: "",
  period_start: "",
  period_end: "",
  cancellation_purpose: "",
  mwh_cancelled: null,
  isCollapsed: false,
});

// When present, mwh_cancelled must be a positive integer — the backend's total_mwh is `int | None`,
// so a fractional value is rejected on both save routes (PUT /statements/<id> and create-from-chat).
// A cleared/empty field passes here (isStatementValid still requires a value for the chat route).
const isMwhCancelledValid = computed(() => {
  const raw = statementForm.value.mwh_cancelled;
  if (raw == null || String(raw).trim() === "") {
    return true;
  }
  const mwhValue = Number(raw);
  return Number.isInteger(mwhValue) && mwhValue > 0;
});

const isStatementValid = computed(() => {
  const s = statementForm.value;
  return Boolean(
    s.beneficiary_name?.trim() &&
    s.registry_code?.trim() &&
    s.registry_country_region &&
    s.period_start &&
    s.period_end &&
    s.cancellation_purpose?.trim() &&
    s.mwh_cancelled &&
    isMwhCancelledValid.value
  );
});
const isAdmin = computed(() => props.isAdmin);
const showNoAuthorizedSellerState = computed(
  () => !loadingCompanies.value && availableCompanies.value.length === 0
);
const isUsingExistingCancellation = computed(
  () => selectedExistingCancellationId.value !== null
);

const assignedDeviceIds = computed(
  () => new Set(assignedDevices.value.map((item) => item.device.id))
);
const filteredPickerDevices = computed(() => {
  const query = deviceSearchQuery.value.toLowerCase();
  return props.allDevices.filter(
    (device) =>
      !assignedDeviceIds.value.has(device.id) &&
      (device.name.toLowerCase().includes(query) ||
        device.country.toLowerCase().includes(query) ||
        (device.plant_ecolabel_id ?? "").toLowerCase().includes(query))
  );
});

const resetWizardState = () => {
  uploadedStatement.value = null;
  statementUploadId.value = null;
  statementNotes.value = null;
  assignedDevices.value = [];
  statementForm.value = {
    ...statementForm.value,
    beneficiary_name: "",
    registry_code: "",
    registry_country_region: "",
    period_start: "",
    period_end: "",
    mwh_cancelled: null,
    cancellation_purpose: "",
  };
};

const mapStatementToForm = (statement: CancellationStatement) => {
  const normalizedRegistryCountry = normalizeRegistryCountryRegion(
    statement.country
  );

  statementForm.value = {
    ...statementForm.value,
    beneficiary_name: statement.beneficiary_name ?? "",
    registry_code: statement.registry_code ?? "",
    registry_country_region: normalizedRegistryCountry ?? "",
    period_start: statement.period_start ?? "",
    period_end: statement.period_end ?? "",
    mwh_cancelled: statement.total_mwh ?? null,
    cancellation_purpose: (statement.purpose as string) || "",
  };
};

const formatExistingCancellationLabel = (
  cancellation: CompanyCancellationSummary
) => {
  const periodStart = cancellation.period_start || "?";
  const periodEnd = cancellation.period_end || "?";
  return `#${cancellation.id} - ${cancellation.beneficiary_name} (${cancellation.mwh_cancelled} MWh, ${periodStart} to ${periodEnd})`;
};

const mapAssignedDevicesFromStatement = (statement: CancellationStatement) => {
  const deviceMap = new Map<number, AssignedDevice>();
  for (const linked of statement.devices) {
    const fallbackDevice = props.allDevices.find(
      (d) => d.id === linked.device_id
    );
    if (!fallbackDevice) continue;
    deviceMap.set(linked.device_id, {
      device: fallbackDevice,
      mwh: linked.mwh_attributed,
      confidence: 0,
    });
  }
  assignedDevices.value = Array.from(deviceMap.values());
};

// The chat turn emits the same payload the legacy upload produced: statement form fields, device
// matches, and the id of the document uploaded inside the conversation. `null` on chat reset.
const handleChatResult = (data: unknown | null) => {
  if (!data) {
    resetWizardState();
    return;
  }

  try {
    const result = data as EcolabelChatResult;
    if (
      !result ||
      typeof result !== "object" ||
      !result.form_data ||
      !result.ai_matches
    ) {
      throw new Error("Invalid chat result payload structure");
    }

    statementUploadId.value = result.statement_upload_id;
    statementNotes.value = result.form_data.notes ?? null;

    statementForm.value = {
      ...statementForm.value,
      beneficiary_name: result.form_data.beneficiary_name ?? "",
      registry_code: result.form_data.registry_code ?? "",
      registry_country_region:
        normalizeRegistryCountryRegion(result.form_data.country) ?? "",
      period_start: result.form_data.period_start ?? "",
      period_end: result.form_data.period_end ?? "",
      mwh_cancelled: result.form_data.total_mwh ?? null,
      cancellation_purpose: result.form_data.purpose ?? "",
    };

    const deviceMap = new Map<number, AssignedDevice>();
    for (const match of result.ai_matches) {
      deviceMap.set(match.device.id, {
        device: match.device,
        mwh: match.suggested_mwh ?? null,
        confidence: match.confidence ?? 0,
      });
    }
    assignedDevices.value = Array.from(deviceMap.values());
  } catch (error) {
    globalErrorHandler(error);
    resetWizardState();
  }
};

const createFromExistingCancellation = async () => {
  if (!selectedCompanyId.value || !selectedExistingCancellationId.value) return;
  uploading.value = true;
  try {
    const response = await ecolabelsApi.uploadStatement(
      selectedCompanyId.value,
      null,
      selectedExistingCancellationId.value
    );
    uploadedStatement.value = response.data.statement;
    mapStatementToForm(response.data.statement);
    mapAssignedDevicesFromStatement(response.data.statement);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    uploading.value = false;
  }
};

const loadExistingCancellations = async (companyId: number) => {
  loadingCancellations.value = true;
  try {
    const response = await ecolabelsApi.listAvailableCancellations(companyId);
    existingCancellations.value = response.data.cancellations;
  } finally {
    loadingCancellations.value = false;
  }
};

const loadAvailableCompanies = async () => {
  loadingCompanies.value = true;
  try {
    const response = await ecolabelsApi.listAuthorizedSellerCompanies();
    availableCompanies.value = response.data.companies;

    if (
      selectedCompanyId.value === null ||
      !availableCompanies.value.some(
        (company) => company.id === selectedCompanyId.value
      )
    ) {
      selectedCompanyId.value = availableCompanies.value[0]?.id ?? null;
    }
  } finally {
    loadingCompanies.value = false;
  }
};

const handleCancellationSourceChange = () => {
  resetWizardState();
  if (selectedExistingCancellationId.value) {
    void createFromExistingCancellation();
  }
};

const createCompany = async () => {
  if (
    !props.isAdmin ||
    !newCompanyName.value.trim() ||
    !newCompanyEmail.value.trim()
  )
    return;
  creatingCompany.value = true;
  try {
    const response = await ecolabelsApi.createAuthorizedSellerCompany(
      newCompanyName.value.trim(),
      newCompanyEmail.value.trim()
    );
    const created = response.data;
    availableCompanies.value = [...availableCompanies.value, created].sort(
      (a, b) => a.name.localeCompare(b.name)
    );
    selectedCompanyId.value = created.id;
    newCompanyName.value = "";
    newCompanyEmail.value = "";
    globalSuccessHandler(t("ecolabels.upload_wizard.company_created"), "");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    creatingCompany.value = false;
  }
};

const handleStatementUpdate = (updatedStatement: IndividualCancelStatement) => {
  statementForm.value = updatedStatement;
};

const updateAssignedMwh = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  assignedDevices.value[index].mwh =
    target.value === "" ? null : Number(target.value);
};

const removeAssignedDevice = (index: number) => {
  assignedDevices.value.splice(index, 1);
};

const addDeviceFromPicker = (device: EcolabelDevice) => {
  assignedDevices.value.push({ device, mwh: null, confidence: 0 });
  showDevicePicker.value = false;
  deviceSearchQuery.value = "";
};

const getConfidenceBadge = (confidence: number) => {
  if (confidence >= 0.8) return "badge bg-success";
  if (confidence >= 0.5) return "badge bg-warning text-dark";
  return "badge bg-danger";
};

const buildStatementPayload = () => {
  const deviceLinks = assignedDevices.value.map((item) => ({
    device_id: item.device.id,
    mwh_attributed: item.mwh ?? null,
  }));

  return {
    company_id: selectedCompanyId.value!,
    beneficiary_name: statementForm.value.beneficiary_name || null,
    registry_code: statementForm.value.registry_code || null,
    country: statementForm.value.registry_country_region || null,
    period_start: statementForm.value.period_start || null,
    period_end: statementForm.value.period_end || null,
    purpose: statementForm.value.cancellation_purpose || null,
    // Preserve an explicit 0 (truthiness would wrongly null it out), but treat a cleared/empty field
    // as null rather than Number("") === 0.
    total_mwh:
      statementForm.value.mwh_cancelled != null &&
      String(statementForm.value.mwh_cancelled).trim() !== ""
        ? Number(statementForm.value.mwh_cancelled)
        : null,
    notes: statementNotes.value,
    device_links: deviceLinks,
  };
};

const saveStatement = async () => {
  if (!selectedCompanyId.value) return;
  saving.value = true;
  try {
    if (isUsingExistingCancellation.value) {
      if (!uploadedStatement.value) return;
      await ecolabelsApi.updateStatement(
        uploadedStatement.value.id,
        buildStatementPayload()
      );
    } else {
      if (!statementUploadId.value) return;
      await ecolabelsApi.createStatementFromChat({
        ...buildStatementPayload(),
        statement_upload_id: statementUploadId.value,
      });
    }
    globalSuccessHandler(t("ecolabels.upload_wizard.success"), "");
    emit("uploaded");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    saving.value = false;
  }
};

onMounted(async () => {
  try {
    await loadAvailableCompanies();
    if (selectedCompanyId.value) {
      await loadExistingCancellations(selectedCompanyId.value);
    }
  } catch (error) {
    globalErrorHandler(error);
  }
});

watch(selectedCompanyId, async (companyId) => {
  selectedExistingCancellationId.value = null;
  resetWizardState();
  if (!companyId) {
    existingCancellations.value = [];
    return;
  }
  try {
    await loadExistingCancellations(companyId);
  } catch (error) {
    globalErrorHandler(error);
  }
});
</script>

<style scoped lang="scss">
.picker-list {
  max-height: 220px;
  overflow-y: auto;
}

.ekoenergy-logo {
  max-width: 280px;
  width: 100%;
  height: auto;
}

:deep(.ecolabel-matching-datatable) {
  .p-datatable-thead > tr > th {
    background-color: var(--phoenix-body-bg);
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-bottom: 1px solid var(--phoenix-border-color);
  }

  .p-datatable-tbody > tr > td {
    border-bottom: 1px solid var(--phoenix-border-color);
    font-size: 0.875rem;
  }
}
</style>
